import React, { useState, useEffect } from 'react';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { ConsentCategory, ConsentStatus, CONSENT_PREFERENCES } from '@/types/consent';
import { Link } from 'react-router-dom';

const CookieConsentBanner: React.FC = () => {
  const {
    consent,
    bannerState,
    acceptAll,
    rejectAll,
    acceptSelected,
    toggleBanner,
    hideBanner,
    isFirstVisit,
  } = useCookieConsent();

  const [localPreferences, setLocalPreferences] = useState<
    Record<ConsentCategory, ConsentStatus>
  >({
    essential: 'granted',
    analytics: 'granted', // Default to granted for better insights
    marketing: 'denied',
  });

  // Initialize local preferences from consent
  useEffect(() => {
    if (consent) {
      setLocalPreferences({
        essential: consent.essential,
        analytics: consent.analytics,
        marketing: consent.marketing,
      });
    }
  }, [consent]);

  // Handle preference toggle
  const handlePreferenceToggle = (category: ConsentCategory) => {
    if (category === 'essential') return; // Can't toggle essential

    setLocalPreferences(prev => ({
      ...prev,
      [category]: prev[category] === 'granted' ? 'denied' : 'granted',
    }));
  };

  // Handle save selected preferences
  const handleSavePreferences = () => {
    const selectedCategories: ConsentCategory[] = [];
    
    (Object.keys(localPreferences) as ConsentCategory[]).forEach(category => {
      if (localPreferences[category] === 'granted') {
        selectedCategories.push(category);
      }
    });

    acceptSelected(selectedCategories);
  };

  // Don't render if banner is hidden
  if (bannerState === 'hidden') {
    return null;
  }

  // Minimized state (cookie icon in corner)
  if (bannerState === 'minimized') {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleBanner}
          className="bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Cookie settings"
          title="Cookie settings"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </button>
      </div>
    );
  }

  // Expanded state (full banner)
  return (
    <div className="fixed inset-x-0 bottom-0 z-50">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={hideBanner}
        aria-hidden="true"
      />
      
      {/* Banner */}
      <div className="relative bg-white border-t border-gray-200 shadow-xl">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            {/* Content */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Cookie Preferences
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {isFirstVisit ? (
                        'Welcome! We use cookies to enhance your experience and analyze site usage.'
                      ) : (
                        'Your cookie preferences can be updated at any time.'
                      )}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={hideBanner}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close cookie banner"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Preferences */}
              <div className="space-y-4 mb-6">
                {CONSENT_PREFERENCES.map(preference => (
                  <div key={preference.id} className="flex items-start space-x-3">
                    <div className="flex items-center h-5 mt-0.5">
                      <input
                        id={`cookie-preference-${preference.id}`}
                        type="checkbox"
                        checked={localPreferences[preference.id] === 'granted'}
                        onChange={() => handlePreferenceToggle(preference.id)}
                        disabled={preference.required}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor={`cookie-preference-${preference.id}`}
                        className="text-sm font-medium text-gray-700"
                      >
                        {preference.title}
                        {preference.required && (
                          <span className="ml-2 text-xs text-gray-500">(Required)</span>
                        )}
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {preference.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Links */}
              <div className="text-sm text-gray-600">
                <p>
                  By continuing to use our site, you acknowledge our{' '}
                  <Link to="/privacy" className="text-primary-600 hover:underline" onClick={hideBanner}>
                    Privacy Policy
                  </Link>
                  . You can change your preferences at any time by clicking the cookie icon.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3 md:w-48">
              <button
                onClick={acceptAll}
                className="w-full btn-primary"
              >
                Accept All
              </button>
              
              <button
                onClick={handleSavePreferences}
                className="w-full btn-secondary"
              >
                Save Preferences
              </button>
              
              <button
                onClick={rejectAll}
                className="w-full text-sm text-gray-600 hover:text-gray-900 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reject All
              </button>
              
              <button
                onClick={hideBanner}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
